"use client";

import { Trans, useLingui } from "@lingui/react/macro";
import { GlobeIcon } from "lucide-react";
import { usePathname, useSearchParams } from "next/navigation";
import { useTransition } from "react";
import { switchLanguage } from "@/app/[lang]/action";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { useRouter } from "@/lib/navigation";

export function LanguageToggle() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const { toast } = useToast();
  const [isPending, startTransition] = useTransition();
  const { t } = useLingui();

  const currentLang = pathname.split("/")[1] || "en";

  const languageLabels = {
    ja: `日本語`,
    en: `English`,
  };
  const handleSwitchLanguage = (locale: string) => {
    startTransition(async () => {
      const { error } = await switchLanguage(
        locale,
        pathname,
        searchParams.toString(),
      );

      // エラーがある場合はトーストで表示
      if (error) {
        toast({
          title: t`Error`,
          description: error,
          variant: "destructive",
        });
        router.refresh();
      }
    });
  };

  // サポートする言語の定義
  const languages = [
    { code: "ja", label: languageLabels.ja },
    { code: "en", label: languageLabels.en },
  ];

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon-sm" disabled={isPending}>
              <GlobeIcon className="h-[1rem] w-[1rem]" />
              <span className="sr-only">
                <Trans>Switch language</Trans>
              </span>
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            <Trans>Switch language</Trans>
          </p>
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end">
        {languages.map((language) => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => handleSwitchLanguage(language.code)}
            className={currentLang === language.code ? "bg-accent" : ""}
            disabled={isPending || currentLang === language.code}
          >
            {language.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
